/**
 * /accept-invitation/:invitationId — Phase 5c-2.
 *
 * Click target of the invitation email. Handles the four reachable
 * states explicitly rather than nested ifs:
 *
 *   - loading       — fetching preview
 *   - invalid       — preview returned 404 / expired / canceled
 *   - notSignedIn   — preview ok, but user has no session — show
 *                     "sign in to accept" CTA preserving the
 *                     invitation URL as returnTo
 *   - wrongEmail    — signed in as a different email than the invite
 *   - readyToAccept — preview ok, session matches, click to accept
 *   - accepted      — accept succeeded; navigating to the workspace's Runs page
 *   - acceptError   — accept threw; show error + retry
 */
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { CheckCircle2, Loader2, XCircle, Mail } from 'lucide-react'
import { Button } from '@/components/ui'
import { AuthLayout } from '@/auth/AuthLayout'
import { useIdentity } from '@/hooks/useIdentity'
import {
  identityAPI,
  invitationsAPI,
  type InvitationPreview,
} from '@/lib/api'

type State =
  | { kind: 'loading' }
  | { kind: 'invalid'; message: string }
  | { kind: 'notSignedIn'; preview: InvitationPreview }
  | { kind: 'wrongEmail'; preview: InvitationPreview; signedInAs: string }
  | { kind: 'readyToAccept'; preview: InvitationPreview; signedInAs: string }
  | { kind: 'accepting' }
  | { kind: 'accepted' }
  | { kind: 'acceptError'; preview: InvitationPreview; message: string }

export function AcceptInvitation() {
  const { invitationId } = useParams<{ invitationId: string }>()
  const navigate = useNavigate()
  const { refreshIdentity } = useIdentity()
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    if (!invitationId) {
      setState({ kind: 'invalid', message: 'Missing invitation token.' })
      return
    }
    let cancelled = false
    void (async () => {
      try {
        const preview = await invitationsAPI.preview(invitationId)
        if (cancelled) return

        if (preview.status && preview.status !== 'pending') {
          setState({
            kind: 'invalid',
            message: `This invitation has already been ${preview.status}.`,
          })
          return
        }
        // Detect signed-in user to branch into match / mismatch /
        // not-signed-in.
        const me = await identityAPI.me()
        if (cancelled) return
        if (!me) {
          setState({ kind: 'notSignedIn', preview })
          return
        }
        if (
          preview.email &&
          preview.email.toLowerCase() !== me.user.email.toLowerCase()
        ) {
          setState({ kind: 'wrongEmail', preview, signedInAs: me.user.email })
          return
        }
        setState({ kind: 'readyToAccept', preview, signedInAs: me.user.email })
      } catch (err) {
        if (cancelled) return
        setState({
          kind: 'invalid',
          message: err instanceof Error ? err.message : 'Failed to load invitation',
        })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [invitationId])

  async function handleAccept() {
    if (
      (state.kind !== 'readyToAccept' && state.kind !== 'acceptError') ||
      !invitationId
    ) {
      return
    }
    const preview = state.preview
    setState({ kind: 'accepting' })
    try {
      await invitationsAPI.accept(invitationId)
      setState({ kind: 'accepted' })
      // Refresh the IdentityProvider's snapshot before navigating.
      // Server-side, the new membership is in place; the React-side
      // identity context still holds the pre-accept org list and
      // would render the destination route blank (or with a stale
      // workspace picker) until the user manually refreshes.
      // Same root cause as the post-login blank-screen issue.
      await refreshIdentity()
      // Land on '/' and let IdentityGate route from the freshly
      // refreshed identity: to the workspace's Runs page if this user has
      // workspace access, or to the no-workspace landing if they
      // don't (invitations grant ORG membership, not workspace
      // access — an admin assigns workspaces separately).
      navigate('/', { replace: true })
    } catch (err) {
      setState({
        kind: 'acceptError',
        preview,
        message: err instanceof Error ? err.message : 'Accept failed',
      })
    }
  }

  // ── Render branches ─────────────────────────────────────────────

  if (state.kind === 'loading') {
    return (
      <AuthLayout title="Loading invitation…">
        <div className="flex justify-center py-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </AuthLayout>
    )
  }

  if (state.kind === 'invalid') {
    return (
      <AuthLayout title="Invitation unavailable" subtitle={state.message}>
        <div className="flex flex-col items-center gap-3 py-2">
          <XCircle className="h-10 w-10 text-destructive" aria-hidden="true" />
          <Link to="/auth/login" className="text-sm text-primary hover:underline">
            Go to sign in
          </Link>
        </div>
      </AuthLayout>
    )
  }

  if (state.kind === 'notSignedIn') {
    return (
      <AuthLayout
        title={`Join ${state.preview.organizationName ?? 'this organization'}`}
        subtitle={`You've been invited as ${state.preview.role}.`}
      >
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Sign in (or sign up) with the email this invitation was sent to —
            <strong className="text-foreground"> {state.preview.email}</strong> —
            then come back to this page.
          </p>
          <div className="flex gap-2">
            <Link
              to={`/auth/login?returnTo=${encodeURIComponent(window.location.pathname)}`}
              className="flex-1"
            >
              <Button className="w-full">Sign in</Button>
            </Link>
            <Link
              to={`/auth/signup?returnTo=${encodeURIComponent(window.location.pathname)}`}
              className="flex-1"
            >
              <Button variant="outline" className="w-full">Sign up</Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    )
  }

  if (state.kind === 'wrongEmail') {
    return (
      <AuthLayout
        title="Wrong email"
        subtitle="This invitation belongs to someone else."
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-destructive/30 bg-destructive/10 p-3">
            <Mail className="h-4 w-4 text-destructive mt-0.5" aria-hidden="true" />
            <div className="text-sm">
              <p className="font-medium">
                Invitation is for {state.preview.email}.
              </p>
              <p className="text-muted-foreground">
                You're signed in as <strong>{state.signedInAs}</strong>. Sign
                out and sign in as the invited email to accept.
              </p>
            </div>
          </div>
          <Link to="/auth/login" className="block">
            <Button className="w-full">Switch accounts</Button>
          </Link>
        </div>
      </AuthLayout>
    )
  }

  if (state.kind === 'readyToAccept' || state.kind === 'acceptError') {
    // `signedInAs` only exists on readyToAccept; on acceptError we
    // already passed the email check so it's not relevant to render.
    const subtitle =
      state.kind === 'readyToAccept'
        ? `As ${state.preview.role}. Signed in as ${state.signedInAs}.`
        : `As ${state.preview.role}.`
    return (
      <AuthLayout
        title={`Join ${state.preview.organizationName ?? 'organization'}`}
        subtitle={subtitle}
      >
        <div className="space-y-4">
          <Button onClick={handleAccept} className="w-full">
            Accept invitation
          </Button>
          {state.kind === 'acceptError' && (
            <p className="text-sm text-destructive" role="alert">{state.message}</p>
          )}
          <p className="text-xs text-muted-foreground text-center">
            Wrong account?{' '}
            <Link to="/auth/login" className="text-primary hover:underline">
              Sign out and switch
            </Link>
          </p>
        </div>
      </AuthLayout>
    )
  }

  if (state.kind === 'accepting') {
    return (
      <AuthLayout title="Joining…">
        <div className="flex justify-center py-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
        </div>
      </AuthLayout>
    )
  }

  // accepted — renders briefly before useNavigate fires
  return (
    <AuthLayout title="You're in" subtitle="Loading your workspace…">
      <div className="flex justify-center py-2">
        <CheckCircle2 className="h-10 w-10 text-success" aria-hidden="true" />
      </div>
    </AuthLayout>
  )
}
